// Trips: every trip online, newest first in month cards, with filters, quick classifying, a review
// mode for working through unclassified trips, and the open trip beside the list.

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { CategoryBadge } from "../components/Category.tsx";
import { FilterSelect, type Option } from "../components/Controls.tsx";
import { Icon } from "../components/Icon.tsx";
import { useToast } from "../components/Toast.tsx";
import { useAccount, useAccountData } from "../data/AccountData.tsx";
import { currencyOf, displayUnit, tripUnit, tripValue, vehicleById, vehicleOf } from "../data/derived.ts";
import { type Category, colorChartTint } from "../domain/categories.ts";
import { formatCount, formatDayTitle, formatDistance, formatMoney, formatMonth, plural } from "../domain/format.ts";
import { type Trip, tripTitle } from "../domain/model.ts";
import { type PeriodKind, contains, periodInterval, periodTitle, presetKinds, customPeriod, type Period } from "../domain/periods.ts";
import { navigate } from "../lib/router.ts";
import { TripDetail } from "./trips/TripDetail.tsx";
import { type QuickPick, TripRow } from "./trips/TripRow.tsx";

const monthsShownAtFirst = 6;

function dateInput(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function parseDateInput(text: string): Date | undefined {
  const [year, month, day] = text.split("-").map(Number);
  return year && month && day ? new Date(year, month - 1, day) : undefined;
}

export function Trips({ tripId, reviewing }: { tripId: string | undefined; reviewing: boolean }) {
  const data = useAccountData();
  const { editTrip, reload } = useAccount();
  const toast = useToast();
  const now = useMemo(() => new Date(), []);

  const [periodKind, setPeriodKind] = useState<PeriodKind>("all");
  const [customFrom, setCustomFrom] = useState(dateInput(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [customTo, setCustomTo] = useState(dateInput(now));
  const [vehicleFilter, setVehicleFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState(reviewing ? "unclassified" : "all");
  const [monthsShown, setMonthsShown] = useState(monthsShownAtFirst);
  /** Trips classified during this review, kept in the list with Undo until Done. */
  const [reviewed, setReviewed] = useState<string[]>([]);
  const reviewTotal = useRef<number | null>(null);

  useEffect(() => {
    if (reviewing) setCategoryFilter("unclassified");
    else setReviewed([]);
  }, [reviewing]);

  const vehicles = useMemo(() => vehicleById(data), [data]);
  const unit = displayUnit(data, vehicleFilter === "all" ? undefined : vehicleFilter);
  const currency = currencyOf(data);

  const period: Period = periodKind === "custom"
    ? customPeriod(parseDateInput(customFrom) ?? now, parseDateInput(customTo) ?? now)
    : { kind: periodKind };
  const interval = periodInterval(period, now);

  // Newest first by start time, whatever order the rows arrived in.
  const inScope = useMemo(() => data.trips.filter((trip) =>
    contains(interval, trip.start)
    && (vehicleFilter === "all" || trip.vehicleId?.toUpperCase() === vehicleFilter.toUpperCase()))
    .sort((a, b) => b.start.getTime() - a.start.getTime()),
  [data.trips, interval?.start.getTime(), interval?.end.getTime(), vehicleFilter]);

  const unclassified = inScope.filter((trip) => !data.catalog.resolve(trip.categoryRaw));
  const visible = inScope.filter((trip) => {
    const category = data.catalog.resolve(trip.categoryRaw);
    if (categoryFilter === "all") return true;
    if (categoryFilter === "unclassified") return !category || (reviewing && reviewed.includes(trip.id));
    return category?.rawValue === categoryFilter;
  });

  if (reviewing && reviewTotal.current === null) reviewTotal.current = unclassified.length;
  if (!reviewing) reviewTotal.current = null;

  const selectedIndex = tripId ? visible.findIndex((trip) => trip.id === tripId) : -1;
  const selected = tripId ? data.trips.find((trip) => trip.id === tripId) : undefined;

  // Reviewing starts on the first unclassified trip.
  useEffect(() => {
    if (reviewing && !tripId && unclassified[0]) navigate(`/trips/${unclassified[0].id}?review=1`, { replace: true });
  }, [reviewing, tripId, unclassified[0]?.id]);

  function open(trip: Trip) {
    navigate(`/trips/${trip.id}${reviewing ? "?review=1" : ""}`);
  }

  function step(offset: number) {
    const target = visible[selectedIndex + offset];
    if (target) open(target);
  }

  // J and K move through the list while reviewing.
  useEffect(() => {
    if (!reviewing) return;
    function onKey(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if ((event.target as HTMLElement).closest("input, textarea, select")) return;
      if (event.key === "j" || event.key === "J") { event.preventDefault(); step(1); }
      if (event.key === "k" || event.key === "K") { event.preventDefault(); step(-1); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  async function classify(trip: Trip, category: Category) {
    const result = await editTrip(trip.id, { categoryRaw: category.rawValue, purpose: trip.purpose, notes: trip.notes });
    if (!result.ok && !result.subscriptionEnded) {
      toast({ tone: "warning", text: `Couldn’t save the category for ${tripTitle(trip)}.`, action: { label: "Retry", run: () => void classify(trip, category) } });
    }
  }

  async function undo(trip: Trip) {
    const result = await editTrip(trip.id, { categoryRaw: null, purpose: trip.purpose, notes: trip.notes });
    if (result.ok) {
      setReviewed((ids) => ids.filter((id) => id !== trip.id));
      open(trip);
    } else if (result.subscriptionEnded) {
      reload();
    }
  }

  /** In review, a pick keeps the trip in the list and moves to the next unclassified one. */
  function picked(trip: Trip) {
    if (!reviewing) return;
    setReviewed((ids) => (ids.includes(trip.id) ? ids : [...ids, trip.id]));
    const next = unclassified.find((candidate) => candidate.id !== trip.id && candidate.start <= trip.start)
      ?? unclassified.find((candidate) => candidate.id !== trip.id);
    if (next) open(next);
  }

  const quickPicks: QuickPick[] = ["business", "personal", "commute"]
    .map((raw) => data.catalog.resolve(raw))
    .filter((category): category is Category => !!category)
    .map((category) => ({ category, chartColor: colorChartTint[category.color] }));

  const months = groupByMonth(visible);
  const periodOptions: Option<PeriodKind>[] = [
    { value: "all", label: "All trips" },
    ...presetKinds.map((kind) => ({ value: kind, label: periodTitle({ kind }) })),
    { value: "custom", label: "Custom…" },
  ];
  const vehicleOptions: Option<string>[] = [{ value: "all", label: "All vehicles" }, ...data.vehicles.map((vehicle) => ({ value: vehicle.id, label: vehicle.name }))];
  const categoryOptions: Option<string>[] = [
    { value: "all", label: "All" },
    { value: "unclassified", label: "Unclassified" },
    ...data.catalog.choices().map((category) => ({ value: category.rawValue, label: category.name })),
  ];
  const classifiedCount = (reviewTotal.current ?? 0) - unclassified.length;
  const filtersShown = [
    periodKind === "all" ? null : periodTitle(period),
    vehicleFilter === "all" ? null : vehicleOptions.find((option) => option.value === vehicleFilter)?.label,
    categoryFilter === "all" ? null : categoryOptions.find((option) => option.value === categoryFilter)?.label,
  ].filter(Boolean);

  return (
    <>
      <div className="cv-head">
        <div>
          <h1 className="cv-title">{reviewing ? "Classify Trips" : "Trips"}</h1>
          <p className="cv-sub">{plural(visible.length, "trip")}{filtersShown.length === 0 ? " online · newest first" : ` · ${filtersShown.join(" · ")}`}</p>
        </div>
        <div className="cv-head-end">
          <div className="cv-filters" role="group" aria-label="Filter trips">
            <FilterSelect label="Period" value={periodKind} options={periodOptions} onChange={(kind) => { setPeriodKind(kind); setMonthsShown(monthsShownAtFirst); }} />
            {periodKind === "custom" && (
              <span className="cv-dates">
                <input type="date" aria-label="From" value={customFrom} onChange={(event) => setCustomFrom(event.target.value)} />
                <input type="date" aria-label="Through" value={customTo} onChange={(event) => setCustomTo(event.target.value)} />
              </span>
            )}
            {data.vehicles.length > 1 && <FilterSelect label="Vehicle" value={vehicleFilter} options={vehicleOptions} onChange={setVehicleFilter} />}
            <FilterSelect label="Category" value={categoryFilter} options={categoryOptions} onChange={(value) => { setCategoryFilter(value); if (reviewing && value !== "unclassified") navigate("/trips"); }} />
          </div>
        </div>
      </div>

      <div className={`cv-split${selected ? " has-detail" : ""}`}>
        <section className="cv-list" aria-label="Trips by month">
          {reviewing ? (
            <div className="cv-card cv-prompt">
              <span className="cv-badge" style={{ "--tint": "var(--amber-fill)" } as React.CSSProperties}><Icon name="checklist" /></span>
              <div style={{ flex: 1 }}>
                <h3>{unclassified.length === 0 ? "All trips classified" : `${formatCount(classifiedCount)} of ${formatCount(reviewTotal.current ?? 0)} classified`}</h3>
                <div className="cv-progress" style={{ marginTop: 8 }} role="progressbar" aria-label="Trips classified" aria-valuemin={0} aria-valuemax={reviewTotal.current ?? 0} aria-valuenow={classifiedCount}>
                  <span style={{ width: `${Math.max(4, (classifiedCount / Math.max(1, reviewTotal.current ?? 1)) * 100)}%` }} />
                </div>
                <p style={{ marginTop: 6 }}>Pick a category with <span className="cv-kbd">1</span>–<span className="cv-kbd">{Math.min(9, data.catalog.choices().length)}</span>; <span className="cv-kbd">J</span> and <span className="cv-kbd">K</span> move.</p>
              </div>
              <div className="cv-prompt-end">
                <button className="cv-btn cv-btn-primary" type="button" onClick={() => navigate("/trips")}>Done</button>
              </div>
            </div>
          ) : unclassified.length > 0 && (
            <div className="cv-card cv-prompt">
              <span className="cv-badge" style={{ "--tint": "var(--amber-fill)" } as React.CSSProperties}><Icon name="checklist" /></span>
              <div>
                <h3>{plural(unclassified.length, "trip")} to classify</h3>
                <p>Sort them for your mileage log</p>
              </div>
              <div className="cv-prompt-end">
                <button className="cv-btn cv-btn-primary" type="button" onClick={() => navigate("/trips?review=1")}>Review</button>
              </div>
            </div>
          )}

          {visible.length === 0 && (
            <div className="cv-card cv-card-pad" role="status">
              <p>{reviewing ? "Nothing left to classify in these trips." : "No trips match these filters."}</p>
            </div>
          )}

          {months.slice(0, monthsShown).map((month) => {
            const meters = month.trips.reduce((sum, trip) => sum + trip.meters, 0);
            const value = month.trips.reduce((sum, trip) => sum + (tripValue(trip, data) ?? 0), 0);
            const headingId = `month-${month.key}`;
            let lastDay = "";
            return (
              <section key={month.key} className="cv-card cv-month" aria-labelledby={headingId}>
                <header className="cv-month-head">
                  <h2 id={headingId}>{formatMonth(month.trips[0]!.start)}</h2>
                  <div className="cv-month-meta">
                    <span><b>{formatCount(month.trips.length)}</b> {month.trips.length === 1 ? "trip" : "trips"}</span>
                    <span><b>{formatDistance(meters, unit).replace(/ \S+$/, "")}</b> {formatDistance(meters, unit).split(" ").at(-1)}</span>
                    {value > 0 && <span className="val">{formatMoney(value, currency)} deductible</span>}
                  </div>
                </header>
                {month.trips.map((trip) => {
                  const day = trip.start.toDateString();
                  const dayStart = day !== lastDay;
                  lastDay = day;
                  const category = data.catalog.resolve(trip.categoryRaw);
                  return (
                    <Fragment key={trip.id}>
                    {dayStart && <h3 className="cv-day-head">{formatDayTitle(trip.start)}</h3>}
                    <TripRow
                      trip={trip}
                      category={category}
                      unit={tripUnit(trip, vehicles, unit)}
                      value={tripValue(trip, data)}
                      currencyCode={currency}
                      vehicleName={data.vehicles.length > 1 ? vehicleOf(trip, vehicles)?.name : undefined}
                      dayStart={dayStart}
                      isSelected={trip.id === tripId}
                      quickPicks={quickPicks}
                      onOpen={() => open(trip)}
                      onClassify={(choice) => { void classify(trip, choice); picked(trip); }}
                      undo={reviewing && reviewed.includes(trip.id) && category ? () => void undo(trip) : undefined}
                    />
                    </Fragment>
                  );
                })}
              </section>
            );
          })}

          {months.length > monthsShown && (
            <button className="cv-btn cv-btn-secondary" type="button" style={{ alignSelf: "center" }} onClick={() => setMonthsShown((count) => count + monthsShownAtFirst)}>
              Show earlier months
            </button>
          )}
        </section>

        {selected ? (
          <TripDetail
            key={selected.id}
            trip={selected}
            data={data}
            reviewing={reviewing}
            onClose={() => navigate(reviewing ? "/trips?review=1" : "/trips")}
            onNewer={selectedIndex > 0 ? () => step(-1) : undefined}
            onOlder={selectedIndex >= 0 && selectedIndex < visible.length - 1 ? () => step(1) : undefined}
            onPicked={() => picked(selected)}
          />
        ) : (
          <aside className="cv-card cv-detail cv-card-pad is-placeholder" aria-label="Trip details" style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <CategoryBadge category={undefined} />
            <p className="cv-hint">Select a trip to see its route and details.</p>
          </aside>
        )}
      </div>
    </>
  );
}

function groupByMonth(trips: readonly Trip[]): { key: string; trips: Trip[] }[] {
  const groups: { key: string; trips: Trip[] }[] = [];
  for (const trip of trips) {
    const key = `${trip.start.getFullYear()}-${trip.start.getMonth() + 1}`;
    const last = groups.at(-1);
    if (last?.key === key) last.trips.push(trip);
    else groups.push({ key, trips: [trip] });
  }
  return groups;
}
